import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { ButtonComp, TextInputComp, PhoneInputComp } from "../../../components";
import { PublicInvestorAuthLayout } from "../../../components/PublicInvestor/PublicInvestorAuthLayout";
import InvestorOnboardingStepper from "../../../components/Investor/InvestorOnboardingStepper";
import { investorButtonProps } from "../../../components/Investor/investorButtonStyles";
import { DEFAULT_INVESTOR_ONBOARDING_FLOW } from "../../../config/investorOnboarding/defaultInvestorOnboardingFlow";
import {
  getInvestorQueryParams,
  withInvestorQuery,
} from "../../../utils/investorRouteHelpers";
import { useInvestorSignupMutation } from "../../../store/investor/investorAuthApi";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentCompanyId, setToken, setUserData, setUserType } from "../../../store/auth/index.js";
import { resolvePostInvestorAuthPath } from "../../../utils/investorPostAuthRedirect";
import { clearLegacyInvestorMockStorage } from "../../../utils/investorMockStorageCleanup";
import { useCompanyCapabilities, CAPABILITY_KEYS } from "../../../hooks/useCompanyCapabilities";
import { usePublicInvestorCompany } from "../../../hooks/usePublicInvestorCompany";
import { extractCapabilitiesFromCoopData } from "../../../utils/investorCapabilities";

const phoneRegExp = /^\+?[1-9]\d{7,14}$/;

const validationSchema = Yup.object({
  first_name: Yup.string().trim().required("First name is required").min(2),
  last_name: Yup.string().trim().required("Last name is required").min(2),
  email: Yup.string().trim().email("Enter a valid email").required("Email is required"),
  phone: Yup.string().required("Phone number is required").matches(phoneRegExp, "Enter a valid phone number"),
  password: Yup.string().required("Password is required").min(8, "At least 8 characters"),
  password_confirmation: Yup.string()
    .required("Confirm your password")
    .oneOf([Yup.ref("password")], "Passwords must match"),
});

export default function InvestorSignupPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const companyId = useSelector(selectCurrentCompanyId);
  const { companyDetails, isLoadingCoop, isCoopSuccess } = usePublicInvestorCompany();
  const { investorPortal, investorSignupEnabled } = useCompanyCapabilities();
  const [investorSignup] = useInvestorSignupMutation();
  const [searchParams] = useSearchParams();
  const preservedQuery = getInvestorQueryParams(searchParams);
  const onboardingFlow = DEFAULT_INVESTOR_ONBOARDING_FLOW;

  const loginPath = withInvestorQuery("/login", preservedQuery);
  const memberSignupPath = "/member-signup";

  const capsFromCompany = extractCapabilitiesFromCoopData(companyDetails);
  const signupAllowed = Boolean(
    (capsFromCompany?.[CAPABILITY_KEYS.INVESTOR_PORTAL] ?? investorPortal) === true &&
      (capsFromCompany?.investorSignupEnabled ?? investorSignupEnabled) === true
  );
  const capabilitiesResolved = Boolean(isCoopSuccess && companyDetails);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      if (!(capsFromCompany?.[CAPABILITY_KEYS.INVESTOR_PORTAL] ?? investorPortal)) {
        toast.error("Investor portal is not enabled for this cooperative.");
        return;
      }
      if (!(capsFromCompany?.investorSignupEnabled ?? investorSignupEnabled)) {
        toast.error("Public investor signup is not enabled for this cooperative.");
        return;
      }
      if (!companyId) {
        toast.error("Company information not loaded. Please refresh and try again.");
        return;
      }

      const data = await investorSignup({
        company_id: companyId,
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        phone: values.phone,
        password: values.password,
        password_confirmation: values.password_confirmation,
        signup_source: "public_signup",
      }).unwrap();

      if (data?.access_token) {
        clearLegacyInvestorMockStorage();
        dispatch(setToken(data.access_token));
        dispatch(setUserType(data.user_type || "investor"));
        dispatch(setUserData(data.profile));
        toast.success("Account created successfully");
        navigate(
          resolvePostInvestorAuthPath(
            { token: data.access_token, profile: data.profile, kyc_status: data.kyc?.status || "none" },
            preservedQuery
          ),
          { replace: true }
        );
      }
    } catch (error) {
      toast.error(error?.data?.message || error.message || "Signup failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoadingCoop || !capabilitiesResolved) {
    return (
      <PublicInvestorAuthLayout
        title="Create investor account"
        subtitle="Preparing signup…"
        backTo={loginPath}
        backLabel="Back to login"
      >
        <div aria-busy="true" className="d-flex flex-column gap-3 py-2">
          <span className="visually-hidden">Loading cooperative settings…</span>
          <div className="placeholder-glow">
            <span className="placeholder col-8 rounded d-block mb-2" style={{ height: 18 }} />
            <span className="placeholder col-12 rounded d-block mb-3" style={{ height: 42 }} />
            <span className="placeholder col-12 rounded d-block mb-3" style={{ height: 42 }} />
            <span className="placeholder col-12 rounded d-block mb-3" style={{ height: 42 }} />
            <span className="placeholder col-6 rounded d-block" style={{ height: 44 }} />
          </div>
        </div>
      </PublicInvestorAuthLayout>
    );
  }

  if (!signupAllowed) {
    return (
      <PublicInvestorAuthLayout
        title="Investor signup unavailable"
        subtitle="This cooperative does not accept standalone investor registrations."
        backTo={loginPath}
        backLabel="Back to login"
      >
        <div className="d-flex flex-column gap-3">
          <p className="mb-0 text-sm text-[#525252]">
            If you are a cooperative member, sign in with your member account, or join the cooperative
            instead.
          </p>
          <div className="d-flex flex-column gap-2">
            <Link
              to={loginPath}
              className="fw-semibold text-[var(--color-secondary,#31778c)] hover:underline"
            >
              Go to login
            </Link>
            <Link
              to={memberSignupPath}
              className="fw-semibold text-[var(--color-secondary,#31778c)] hover:underline"
            >
              Join as cooperative member
            </Link>
          </div>
        </div>
      </PublicInvestorAuthLayout>
    );
  }

  return (
    <PublicInvestorAuthLayout
      title="Create investor account"
      subtitle="Register for investments, marketplace, and property requests. This is separate from cooperative membership."
      backTo={loginPath}
      backLabel="Back to login"
      topLinks={
        <span className="hidden sm:inline">
          Already registered?{" "}
          <Link to={loginPath} className="fw-semibold text-[var(--color-secondary,#31778c)] hover:underline">
            Sign in
          </Link>
        </span>
      }
    >
      <InvestorOnboardingStepper
        flow={onboardingFlow}
        progress={{ account: false }}
        activeStepId="account"
      />

      <Formik
        initialValues={{
          first_name: "",
          last_name: "",
          email: "",
          phone: "",
          password: "",
          password_confirmation: "",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, touched, errors, isSubmitting }) => (
          <Form className="d-flex flex-column gap-3">
            <div className="row g-3">
              <div className="col-sm-6">
                <TextInputComp labelText="First name" name="first_name" placeholder="First name" />
              </div>
              <div className="col-sm-6">
                <TextInputComp labelText="Last name" name="last_name" placeholder="Last name" />
              </div>
            </div>

            <TextInputComp labelText="Email" name="email" type="email" placeholder="you@email.com" />

            <PhoneInputComp
              LabelText="Phone number"
              name="phone"
              value={values.phone}
              onChange={(phone) => setFieldValue("phone", phone)}
              errors={touched.phone && errors.phone}
            />

            <div className="row g-3">
              <div className="col-sm-6">
                <TextInputComp
                  labelText="Password"
                  name="password"
                  type="password"
                  placeholder="At least 8 characters"
                />
              </div>
              <div className="col-sm-6">
                <TextInputComp
                  labelText="Confirm password"
                  name="password_confirmation"
                  type="password"
                  placeholder="Re-enter password"
                />
              </div>
            </div>

            <div className="pt-1">
              <ButtonComp
                type="submit"
                btnText={isSubmitting ? "Creating account..." : "Create investor account"}
                loading={isSubmitting}
                {...investorButtonProps.primary}
              />
            </div>

            <div className="pt-2 text-sm text-[#525252] d-flex flex-column gap-2 border-t border-[#e0e0e0] mt-1">
              <p className="mb-0 sm:hidden">
                Already registered?{" "}
                <Link to={loginPath} className="fw-semibold text-[var(--color-secondary,#31778c)] hover:underline">
                  Sign in
                </Link>
              </p>
              <p className="mb-0">
                Looking for cooperative membership?{" "}
                <Link to={memberSignupPath} className="fw-semibold text-[var(--color-secondary,#31778c)] hover:underline">
                  Join as cooperative member
                </Link>
              </p>
            </div>
          </Form>
        )}
      </Formik>
    </PublicInvestorAuthLayout>
  );
}
